import * as React from 'react';

import 'dayjs/locale/ja';
import {
  FormHelperText,
  TextField,
  TextFieldProps,
  Typography
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import {
  black,
  errorColor,
  gray10,
  gray100,
  gray80
} from '../../constants/Colors';
import { formLabelWrapper } from '../../constants/Location';

import type { FieldError } from 'react-hook-form';

interface Props {
  labelText?: string;
  requiredText?: string;
  isRequired: boolean;
  error?: boolean;
  helperText?: string | FieldError | undefined;
  fieldType?: string;
  width?: string;
  formMb?: string | undefined;
  placeholder?: string;
  disabled?: boolean;
  disabledColor?: string;
  value: Date | null | undefined;
  setValue: (
    value: Date | null,
    keyboardInputValue?: string | undefined
  ) => void;
  flex?: number;
}

const CustomDatePicker = React.forwardRef(
  (props: Props): React.ReactElement | JSX.Element => {
    const locale = 'ja' as const;
    const {
      labelText,
      requiredText,
      isRequired,
      error,
      helperText,
      formMb,
      placeholder,
      width,
      disabled,
      disabledColor,
      setValue,
      value,
      flex,
      ...rest
    } = props;

    const theme = createTheme({
      components: {
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              '& .MuiSvgIcon-root': {
                color: disabled && disabledColor ? black : 'inherit'
              }
            }
          }
        }
      }
    });

    function inputBorderFocusColor(): string {
      if (disabled) return 'null';
      return error ? errorColor : gray100;
    }

    function inputBorderColor(): string {
      return error ? errorColor : gray80;
    }

    let inputWidth;
    let inputSpWidth;
    if (width === 'search') {
      inputWidth = '152px';
      inputSpWidth = '134px';
    } else {
      inputWidth = width;
      inputSpWidth = width;
    }

    const styles = {
      datePicker: {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: inputBorderColor()
          },
          '&.Mui-focused fieldset': {
            borderColor: inputBorderFocusColor()
          },
          '&:hover fieldset': {
            borderColor: inputBorderFocusColor()
          }
        },
        '@media screen and (max-width:480px)': {
          width: inputSpWidth ?? '148px'
        },
        bgcolor: disabled ? gray10 : '#FFF',
        width: inputWidth ?? '180px'
      },
      formLabelWrapper,
      label: {
        fontSize: '14px'
      },
      required: {
        color: errorColor,
        fontSize: '12px',
        marginLeft: '10px'
      },
      formMb: { marginBottom: formMb ?? '8px' }
    };

    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    return (
      <Typography flex={flex} sx={styles.formMb} align="left" component="div">
        <Typography align="left" sx={styles.formLabelWrapper} component="div">
          {labelText ? (
            <Typography color={black} sx={styles.label} component="span">
              {labelText}
            </Typography>
          ) : (
            ''
          )}
          {isRequired ? (
            <Typography component="span" sx={styles.required}>
              {requiredText}
            </Typography>
          ) : (
            ''
          )}
        </Typography>
        <ThemeProvider theme={theme}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={locale}
          >
            <DatePicker
              orientation="portrait"
              renderInput={(params: TextFieldProps) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    disabled,
                    placeholder,
                    style:
                      disabled && disabledColor
                        ? { WebkitTextFillColor: black }
                        : {}
                  }}
                  size="small"
                  sx={styles.datePicker}
                />
              )}
              inputFormat="YYYY/MM/DD"
              onChange={setValue}
              value={value}
              disabled={disabled}
              {...rest}
            />
          </LocalizationProvider>
        </ThemeProvider>
        <Typography sx={{ height: '20px' }}>
          <FormHelperText error={error}>{textfieldHelper()}</FormHelperText>
        </Typography>
      </Typography>
    );
  }
);
CustomDatePicker.displayName = 'CustomDatePicker';
export default CustomDatePicker;
